/**
 * Pure rules for the company contacts manager window (owner, 2026-10-16).
 *
 * Mirrors the server's contact write schema (`contacts.schema.ts`) and the
 * validation in `ContactFormModal`:
 *
 *   name       VARCHAR(240), trimmed. A PATCH cannot clear it (min 1), so a
 *              blank full name is never sent: validation refuses it first.
 *   firstName  120, lastName 120, title 120: '' clears (server maps '' → null).
 *   email      255, lowercased by the server, so a case-only change is not one.
 *   phone 32, mobile 32: length only, no format check server-side.
 *
 * PATCH sends only changed keys; absent = untouched, null = clear.
 */
import { CONTACT_NAME_LABELS } from '@/lib/contact-name';
import type { Contact, ContactInput } from '@/lib/types';

export type InlineField = 'name' | 'firstName' | 'lastName' | 'title' | 'email' | 'phone' | 'mobile';

export const INLINE_FIELD_LABELS: Record<InlineField, string> = {
  name: CONTACT_NAME_LABELS.name,
  firstName: CONTACT_NAME_LABELS.firstName,
  lastName: CONTACT_NAME_LABELS.lastName,
  title: 'Title',
  email: 'Email',
  phone: 'Phone',
  mobile: 'Mobile',
};

/** Server column limits (`contacts.schema.ts`). */
export const INLINE_FIELD_LIMITS: Record<InlineField, number> = {
  name: 240,
  firstName: 120,
  lastName: 120,
  title: 120,
  email: 255,
  phone: 32,
  mobile: 32,
};

const INLINE_FIELDS: readonly InlineField[] = ['name', 'firstName', 'lastName', 'title', 'email', 'phone', 'mobile'];

/* Same loose pattern as ContactFormModal: the server checks the format. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function fieldValue(contact: Contact, field: InlineField): string {
  return contact[field] ?? '';
}

function sameValue(field: InlineField, stored: string, next: string): boolean {
  if (field === 'email') return stored.trim().toLowerCase() === next.toLowerCase();
  return stored.trim() === next;
}

/**
 * Only the keys in `draft` whose trimmed value differs from the stored one.
 * '' clears an optional field (null); a blank full name is never sent.
 * Returns null when nothing changed.
 */
export function buildContactPatch(
  contact: Contact,
  draft: Partial<Record<InlineField, string>>,
): ContactInput | null {
  const patch: ContactInput = {};
  let changed = false;
  for (const field of INLINE_FIELDS) {
    const raw = draft[field];
    if (raw === undefined) continue;
    const next = raw.trim();
    if (sameValue(field, fieldValue(contact, field), next)) continue;
    if (field === 'name') {
      if (next === '') continue;
      patch.name = next;
    } else {
      patch[field] = next === '' ? null : next;
    }
    changed = true;
  }
  return changed ? patch : null;
}

/**
 * Error text per field, for the keys present in `draft`.
 *
 * Full name is always required. First name is required on a create (no
 * `contact`), and on an edit only when the stored row had one or the name
 * parts are being changed, so a legacy row with no first name can still be
 * saved (ContactFormModal's `firstNameRequiredFor`).
 */
export function validateContactDraft(
  draft: Partial<Record<InlineField, string>>,
  contact?: Contact,
): Partial<Record<InlineField, string>> {
  const errors: Partial<Record<InlineField, string>> = {};

  if (draft.firstName !== undefined && draft.firstName.trim() === '') {
    const partsChanged =
      contact !== undefined &&
      (draft.firstName.trim() !== fieldValue(contact, 'firstName').trim() ||
        (draft.lastName !== undefined && draft.lastName.trim() !== fieldValue(contact, 'lastName').trim()));
    const required = contact === undefined || fieldValue(contact, 'firstName').trim() !== '' || partsChanged;
    if (required) errors.firstName = 'Enter the contact’s first name.';
  }
  if (draft.name !== undefined && draft.name.trim() === '') {
    errors.name = 'Enter the contact’s full name.';
  }

  for (const field of INLINE_FIELDS) {
    const raw = draft[field];
    if (raw === undefined || errors[field]) continue;
    const max = INLINE_FIELD_LIMITS[field];
    if (raw.trim().length > max) {
      errors[field] = `${INLINE_FIELD_LABELS[field]} must be ${max} characters or fewer.`;
    }
  }

  const email = draft.email?.trim();
  if (email && !errors.email && !EMAIL_PATTERN.test(email)) {
    errors.email = 'Enter a valid email address.';
  }

  return errors;
}

/** Primary contact first, then by full name (case-insensitive), then id. */
export function orderContactsForManager(contacts: Contact[], primaryContactId: number | null): Contact[] {
  return [...contacts].sort((a, b) => {
    const aPrimary = a.id === primaryContactId;
    const bPrimary = b.id === primaryContactId;
    if (aPrimary !== bPrimary) return aPrimary ? -1 : 1;
    const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return byName !== 0 ? byName : a.id - b.id;
  });
}
